import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Copy, RotateCcw, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import needsData from '../data/needs.json';
import feelingsData from '../data/feelings.json';

// Beautiful word cloud sentence builder for Page 5 of the Belonging eBook
export default function BeautifulWordsBuilder({ mode = 'needs-feelings' }) {
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [isMet, setIsMet] = useState(true);
  const [copied, setCopied] = useState(false);

  // Generate the sentence based on selections
  const generateSentence = useCallback(() => {
    if (selectedNeed && selectedFeeling) {
      if (isMet) {
        return `When my need for ${selectedNeed.toLowerCase()} is met, I feel ${selectedFeeling.toLowerCase()}.`;
      } else {
        return `When my need for ${selectedNeed.toLowerCase()} is not met, I feel ${selectedFeeling.toLowerCase()}.`;
      }
    } else if (selectedNeed) {
      return `My need for ${selectedNeed.toLowerCase()}...`;
    } else if (selectedFeeling) {
      return `I feel ${selectedFeeling.toLowerCase()}...`;
    } else if (selectedCategory) {
      return `Exploring the need for ${selectedCategory.toLowerCase()}...`;
    }
    return null;
  }, [selectedNeed, selectedFeeling, selectedCategory, isMet]);

  const handleWordClick = (word, type) => {
    if (type === 'category') {
      if (selectedCategory === word) {
        setSelectedCategory(null);
      } else {
        setSelectedCategory(word);
        setSelectedNeed(word); // Categories are also selectable as needs
      }
    } else if (type === 'need') {
      if (selectedNeed === word) {
        setSelectedNeed(null);
      } else {
        setSelectedNeed(word);
      }
    } else if (type === 'feeling') {
      if (selectedFeeling === word) {
        setSelectedFeeling(null);
      } else {
        setSelectedFeeling(word);
      }
    }
  };

  const reset = () => {
    setSelectedNeed(null);
    setSelectedCategory(null);
    setSelectedFeeling(null);
    setCopied(false);
  };

  const copySentence = () => {
    const sentence = generateSentence();
    if (sentence) {
      navigator.clipboard.writeText(sentence);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sentence = generateSentence();

  // Feelings organized by whether needs are met
  const feelingsWhenMet = feelingsData.needsSatisfied?.categories || [];
  const feelingsWhenNotMet = feelingsData.needsNotSatisfied?.categories || [];
  const currentFeelings = isMet ? feelingsWhenMet : feelingsWhenNotMet;

  // Color palette for categories
  const categoryColors = {
    'Sustenance': 'from-amber-400 to-orange-500',
    'Safety': 'from-blue-400 to-indigo-500',
    'Love': 'from-rose-400 to-pink-500',
    'Empathy': 'from-purple-400 to-violet-500',
    'Rest, Recreation & Play': 'from-cyan-400 to-sky-500',
    'Community': 'from-emerald-400 to-green-500',
    'Creativity': 'from-yellow-400 to-amber-500',
    'Autonomy': 'from-teal-400 to-emerald-500',
    'Meaning & Purpose': 'from-fuchsia-400 to-purple-500',
  };

  const getCategoryColor = (name) => categoryColors[name] || 'from-slate-400 to-slate-500';

  return (
    <div className="relative" data-testid="beautiful-words-builder">
      {/* Sentence Builder Output - Always at top */}
      <div className="sticky top-0 z-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-emerald-500/30 py-6 px-4 mb-8 shadow-2xl">
        <div className="max-w-4xl mx-auto text-center">
          {sentence ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
                <span className="text-amber-400 text-sm font-medium uppercase tracking-wider">Your Expression</span>
                <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
              </div>
              <p className="text-2xl md:text-3xl font-light text-white leading-relaxed tracking-wide">
                {sentence}
              </p>
              <div className="flex items-center justify-center gap-3 mt-4">
                <Button
                  onClick={copySentence}
                  variant="outline"
                  size="sm"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  size="sm"
                  className="border-slate-500/50 text-slate-400 hover:bg-slate-500/20"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-slate-400 text-lg">Select words below to build your expression</p>
              <p className="text-slate-500 text-sm">Click a need, then a feeling to create: "When my need for ___ is met, I feel ___"</p>
            </div>
          )}
        </div>
      </div>

      {/* Met/Not Met Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-slate-800/50 rounded-full p-1 border border-slate-700">
          <button
            onClick={() => setIsMet(true)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              isMet 
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            When Needs Are Met
          </button>
          <button
            onClick={() => setIsMet(false)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              !isMet 
                ? 'bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-lg' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            When Needs Are Not Met
          </button>
        </div>
      </div>

      {/* NEEDS Section - Page 5 */}
      <div className="mb-12">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-emerald-400 mb-2">Universal Human Needs</h2>
          <p className="text-slate-400 text-sm">Click any word or category title to select it</p>
        </div>

        <div className="grid gap-6">
          {needsData.categories.map((category) => (
            <div key={category.name} className="group">
              {/* Category Title - Clickable */}
              <button
                onClick={() => handleWordClick(category.name, 'category')}
                className={`w-full text-left mb-3 px-4 py-2 rounded-lg transition-all ${
                  selectedCategory === category.name || selectedNeed === category.name
                    ? `bg-gradient-to-r ${getCategoryColor(category.name)} text-white shadow-lg transform scale-[1.02]`
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600'
                }`}
                data-testid={`category-${category.name}`}
              >
                <span className="font-semibold text-lg">{category.name}</span>
                {(selectedCategory === category.name || selectedNeed === category.name) && (
                  <span className="ml-2 text-white/80">✓</span>
                )}
              </button>
              
              {/* Words - Flow naturally */}
              <div className="flex flex-wrap gap-2 px-2">
                {category.needs.map((need) => (
                  <button
                    key={need}
                    onClick={() => handleWordClick(need, 'need')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all transform hover:scale-105 ${
                      selectedNeed === need
                        ? `bg-gradient-to-r ${getCategoryColor(category.name)} text-white shadow-lg ring-2 ring-white/30`
                        : 'bg-slate-800/80 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/50'
                    }`}
                    data-testid={`need-${need}`}
                  >
                    {need}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FEELINGS Section - Pages 6-7 */}
      <div className="mt-12">
        <div className="text-center mb-6">
          <h2 className={`text-2xl font-bold mb-2 ${isMet ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isMet ? 'Feelings When Needs Are Met' : 'Feelings When Needs Are NOT Met'}
          </h2>
          <p className="text-slate-400 text-sm">Click any feeling to add it to your expression</p>
        </div>

        <div className="grid gap-6">
          {currentFeelings.map((category) => (
            <div key={category.name} className="group">
              {/* Category Title - Clickable */}
              <button
                onClick={() => handleWordClick(category.name, 'feeling')}
                className={`w-full text-left mb-3 px-4 py-2 rounded-lg transition-all ${
                  selectedFeeling === category.name
                    ? `bg-gradient-to-r ${isMet ? 'from-emerald-500 to-teal-500' : 'from-rose-500 to-red-500'} text-white shadow-lg transform scale-[1.02]`
                    : `bg-slate-800/50 ${isMet ? 'text-emerald-300' : 'text-rose-300'} hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600`
                }`}
                data-testid={`feeling-category-${category.name}`}
              >
                <span className="font-semibold text-lg uppercase tracking-wide">{category.name}</span>
                {selectedFeeling === category.name && (
                  <span className="ml-2 text-white/80">✓</span>
                )}
              </button>
              
              {/* Feeling Words - Flow naturally */}
              <div className="flex flex-wrap gap-2 px-2">
                {category.feelings.map((feeling) => (
                  <button
                    key={feeling}
                    onClick={() => handleWordClick(feeling, 'feeling')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all transform hover:scale-105 ${
                      selectedFeeling === feeling
                        ? `bg-gradient-to-r ${isMet ? 'from-emerald-500 to-teal-500' : 'from-rose-500 to-red-500'} text-white shadow-lg ring-2 ring-white/30`
                        : `bg-slate-800/80 ${isMet ? 'text-emerald-200' : 'text-rose-200'} hover:text-white hover:bg-slate-700 border border-slate-700/50`
                    }`}
                    data-testid={`feeling-${feeling}`}
                  >
                    {feeling}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instruction Footer */}
      <div className="mt-12 text-center">
        <div className="inline-block bg-slate-800/50 backdrop-blur-sm rounded-xl px-6 py-4 border border-slate-700">
          <p className="text-slate-300 text-sm leading-relaxed max-w-xl">
            <span className="text-emerald-400 font-semibold">Pro tip:</span> Try saying your sentence out loud. 
            Notice how it feels different from blame or criticism. This is the language of connection.
          </p>
        </div>
      </div>
    </div>
  );
}
